import pluralize from 'pluralize';
import { memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTrackClickedSubmitCollectionForm, useTrackSubmittedCollectionForm } from '@air/analytics';
import { ShortUrl } from '@air/api';
import { SendIcon } from '@air/design-icons';
import { Airror, MAX_ANCESTOR_COUNT_EXCEEDED, MAX_FORM_SESSION_ITEMS_EXCEEDED, matchesAirror } from '@air/errors';
import {
  contentCollectionAllUploadsCompletedSelector,
  contentCollectionClipsSelector,
  contentCollectionClipsWithUploadCountSelector,
  contentCollectionFormIsValidSelector,
  contentCollectionFormSelector,
  contentCollectionSessionIdSelector,
  contentCollectionSubmitterNameSelector,
  setAttemptedSubmissionAction,
  setSubmittedContentCollectionClipsAction,
} from '@air/feature-content-collection/redux';
import { Button } from '@air/primitive-button';
import { useBreakpointsContext } from '@air/provider-media-query';
import { useShortIdContext } from '@air/provider-short-id';
import { useToasts } from '@air/provider-toast';
import { resetUploadsAction, uploadArraySelector } from '@air/redux-uploader';
import { callRequestWithRetry } from '@air/upload-utils';
import { reportErrorToBugsnag } from '@air/utils-error';

import { FORMATTED_MAX_FORM_SESSION_ITEMS } from '~/components/ContentCollection/constants';
import { useFormSessionSubmitterTask } from '~/components/ContentCollection/hooks/useFormSessionSubmitterTask';
import { calculateItemCountFromContentCollectionUploads } from '~/components/ContentCollection/utils';
import { useAirStore } from '~/utils/ReduxUtils';

// Below this item count we call submitFormSession and wait on the response; at or above we dispatch a
// FormSessionSubmitter task and resolve completion via WSS (see useFormSessionSubmitterTask).
const MAX_INTERACTIVE_SYNC_ITEMS = 500;

export const ContentCollectionSubmitButton = memo(() => {
  const { isAboveMediumScreen } = useBreakpointsContext();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const store = useAirStore();
  const { showToast } = useToasts();
  const clipCount = useSelector(contentCollectionClipsWithUploadCountSelector);
  const allUploadsCompleted = useSelector(contentCollectionAllUploadsCompletedSelector);
  const formIsValid = useSelector(contentCollectionFormIsValidSelector);
  const { shortId } = useShortIdContext();

  const { trackSubmittedCollectionForm } = useTrackSubmittedCollectionForm();
  const { trackClickedSubmitCollectionForm } = useTrackClickedSubmitCollectionForm();

  const completeSubmission = useCallback(() => {
    dispatch(setSubmittedContentCollectionClipsAction({ clips: contentCollectionClipsSelector(store.getState()) }));
    dispatch(resetUploadsAction());
    trackSubmittedCollectionForm({ auto_submit: false });
    setIsSubmitting(false);
  }, [dispatch, store, trackSubmittedCollectionForm]);

  const handleSubmitFailure = useCallback(
    (error: unknown) => {
      setIsSubmitting(false);

      if (matchesAirror(error, MAX_ANCESTOR_COUNT_EXCEEDED)) {
        showToast(
          'The folder structure you’re trying to upload to is too deep. Please simplify your folder structure and try again.',
          { color: 'red' },
        );
        return;
      }

      if (matchesAirror(error, MAX_FORM_SESSION_ITEMS_EXCEEDED)) {
        showToast(
          `The maximum number of items allowed per session is ${FORMATTED_MAX_FORM_SESSION_ITEMS}. For larger uploads, try splitting them into multiple sessions.`,
          { color: 'red' },
        );
        return;
      }

      showToast('Failed to submit items', { color: 'red' });

      const sessionId = contentCollectionSessionIdSelector(store.getState());
      const form = contentCollectionFormSelector(store.getState());
      const itemCount = calculateItemCountFromContentCollectionUploads(uploadArraySelector(store.getState()));

      reportErrorToBugsnag({
        error,
        context: 'ContentCollectionSubmitButton',
        metadata: {
          formId: form.id,
          sessionId,
          itemCount,
          mode: itemCount < MAX_INTERACTIVE_SYNC_ITEMS ? 'interactive' : 'async',
        },
      });
    },
    [showToast, store],
  );

  const handleTaskFailure = useCallback(
    (failureCode: string | undefined) => {
      let error: unknown;

      switch (failureCode) {
        case MAX_ANCESTOR_COUNT_EXCEEDED.type:
          error = new Airror(MAX_ANCESTOR_COUNT_EXCEEDED);
          break;
        case MAX_FORM_SESSION_ITEMS_EXCEEDED.type:
          error = new Airror(MAX_FORM_SESSION_ITEMS_EXCEEDED);
          break;
        default:
          error = new Error(`FormSessionSubmitter task failed: ${failureCode ?? 'unknown'}`);
          break;
      }

      handleSubmitFailure(error);
    },
    [handleSubmitFailure],
  );

  const { dispatchFormSessionSubmitterTask } = useFormSessionSubmitterTask({
    onSucceeded: completeSubmission,
    onFailed: handleTaskFailure,
  });

  const handleSubmit = useCallback(async () => {
    try {
      dispatch(setAttemptedSubmissionAction({ attemptedSubmission: true }));
      trackClickedSubmitCollectionForm();

      const sessionId = contentCollectionSessionIdSelector(store.getState());
      const form = contentCollectionFormSelector(store.getState());
      const name = contentCollectionSubmitterNameSelector(store.getState());

      if (!sessionId || !formIsValid) {
        return;
      }

      setIsSubmitting(true);

      const itemCount = calculateItemCountFromContentCollectionUploads(uploadArraySelector(store.getState()));

      if (itemCount < MAX_INTERACTIVE_SYNC_ITEMS) {
        await callRequestWithRetry(() => ShortUrl.submitFormSession({ shortId, formId: form.id, sessionId, name }));
        completeSubmission();
        return;
      }

      await dispatchFormSessionSubmitterTask({
        formId: form.id,
        sessionId,
        submitterName: name,
      });
    } catch (error) {
      handleSubmitFailure(error);
    }
  }, [
    completeSubmission,
    dispatch,
    dispatchFormSessionSubmitterTask,
    handleSubmitFailure,
    shortId,
    store,
    trackClickedSubmitCollectionForm,
    formIsValid,
  ]);

  return (
    <Button
      appearance="filled"
      color="blue"
      data-testid="CONTENT_COLLECTION_SUBMIT_BUTTON"
      className="rounded-8 ads:disabled:text-metadata disabled:text-gray-10"
      disabled={!clipCount || !allUploadsCompleted || !formIsValid || isSubmitting}
      isLoading={isSubmitting}
      onClick={handleSubmit}
      size={isAboveMediumScreen ? 'large' : 'extra-large'}
      prefix={<SendIcon size="sm" />}
    >
      {`Submit ${pluralize('file', clipCount, true)}`}
    </Button>
  );
});

ContentCollectionSubmitButton.displayName = 'ContentCollectionSubmitButton';
